// 🔗 B290 (AI_129) — QR DE VALIDAÇÃO DE DOCUMENTOS (estilo Memed)
// ─────────────────────────────────────────────────────────────
// Cliente: gera o código de validação, monta o bloco QR (SVG estático) que entra
// no rodapé do PDF assinado e calcula o hash do 2º fator (data de nascimento).
//
// ⚠️ O render do PDF (api/gerar-pdf.js) roda com JavaScript DESLIGADO
// (setJavaScriptEnabled(false)) e sem recursos externos. Por isso o QR precisa ser
// SVG INLINE (não <canvas>, não <img> de URL externa). Usamos o QRCodeSVG do
// `qrcode.react` (já instalado) renderizado para string via renderToStaticMarkup.
//
// O QR aponta para a página pública hepato.app/validar/{codigo}. O conteúdo clínico
// (medicamentos/itens + paciente) só é revelado lá após o 2º fator — ver
// api/_lib/validacaoServidor.js (que ESPELHA o hash daqui).
// ─────────────────────────────────────────────────────────────

import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
import { normalizarDataParaISO } from "../../ia-analise/services/anonimizarDados";

/** Base pública da validação (produção). Override por env em preview/teste. */
export const URL_VALIDACAO_BASE = (
  process.env.REACT_APP_URL_VALIDACAO || "https://hepato.app"
).replace(/\/+$/, "");

/** URL pública que o QR codifica. */
export function urlValidacao(codigo: string): string {
  return `${URL_VALIDACAO_BASE}/validar/${normalizarCodigo(codigo)}`;
}

// Base32 Crockford SEM ambíguos (sem I, L, O, U) → legível p/ digitação. 32 símbolos
// → 5 bits cada; 12 símbolos = 60 bits de entropia. 32 divide 256 (sem viés de módulo).
const ALFABETO = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const TAMANHO = 12;

/** Gera um código imprevisível (60 bits), canônico (12 símbolos, sem hífen). */
export function gerarCodigoValidacao(): string {
  const bytes = new Uint8Array(TAMANHO);
  crypto.getRandomValues(bytes);
  let s = "";
  for (let i = 0; i < TAMANHO; i++) s += ALFABETO[bytes[i] % 32];
  return s;
}

/** Canoniza o código (maiúsculas, só os símbolos do alfabeto) para id/lookup. */
export function normalizarCodigo(codigo: string): string {
  return String(codigo || "")
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");
}

/** Exibição amigável "XXXX-XXXX-XXXX" para impressão/digitação. */
export function formatarCodigoExibicao(codigo: string): string {
  const k = normalizarCodigo(codigo);
  return k.replace(/(.{4})(?=.)/g, "$1-");
}

/** Tipo do 2º fator de validação (B292). */
export type FatorValidacao = "nascimento" | "celular4";

/**
 * Os 4 últimos dígitos do celular (2º fator "celular4"). "" se < 4 dígitos.
 * `slice(-4)` é estável a +55/DDD/máscara. ESPELHA o ultimos4Telefone do servidor.
 */
export function ultimos4Telefone(telefone: string): string {
  const d = String(telefone || "").replace(/\D/g, "");
  return d.length >= 4 ? d.slice(-4) : "";
}

/**
 * Hash do 2º fator. Material = `${codigo}:${digitos}` (SHA-256 hex).
 * `fator` ∈ {"nascimento","celular4"}. ESPELHA api/_lib/validacaoServidor.js.
 * O material de "nascimento" (8 dígitos YYYYMMDD) é idêntico ao B290 original
 * → documentos antigos validam sem migração. "celular4" usa os 4 últimos dígitos.
 */
export async function hashSegundoFator(
  codigo: string,
  fator: FatorValidacao,
  valor: string
): Promise<string> {
  const digits =
    fator === "celular4"
      ? ultimos4Telefone(valor)
      : normalizarDataParaISO(valor).replace(/\D/g, ""); // "YYYYMMDD" | ""
  const material = `${normalizarCodigo(codigo)}:${digits}`;
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(material)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Bloco visual do QR (SVG estático) + código legível, para o rodapé do PDF assinado
 * (entra ao lado do selo grande ICP-Brasil — via concatenação na string do selo).
 */
export function montarBlocoQR(codigo: string): string {
  const url = urlValidacao(codigo);
  const svg = renderToStaticMarkup(
    React.createElement(QRCodeSVG, {
      value: url,
      size: 104,
      level: "M",
      // Quiet zone de 4 módulos (padrão QR): SEM ela muitos leitores de farmácia
      // falham. Fica DENTRO do SVG (branco) → garantida independente do entorno.
      marginSize: 4,
      bgColor: "#ffffff",
      fgColor: "#1a1a1a",
    })
  );
  const cod = formatarCodigoExibicao(codigo);
  return (
    `<div style="display:inline-flex;flex-direction:column;align-items:center;vertical-align:middle;margin-left:18px;font-family:'Helvetica Neue',Arial,sans-serif;">` +
    `<div style="line-height:0;">${svg}</div>` +
    `<div style="font-size:7.5px;color:#555;margin-top:3px;">Confira a autenticidade</div>` +
    `<div style="font-size:10px;font-weight:700;color:#1a3a6e;letter-spacing:0.5px;">${cod}</div>` +
    `<div style="font-size:7.5px;color:#777;">hepato.app/validar</div>` +
    `</div>`
  );
}
